import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import { Tag, Loader2, Package } from 'lucide-react';
import { toast } from 'sonner';
import { printBatchLabels } from '../services/PrinterService';
import { useStore } from '../context/StoreContext';

const stockOf = (p, store) => Math.max(0, store === 'eastridge' ? (p.stock_eastridge ?? 0) : (p.stock_orewa ?? 0));

// Bulk label printing: one row per selected product, copies default to what the active store has in stock
export const LabelPrintDialog = ({ products, store, open, onClose }) => {
  const { storeName: storeNameOf } = useStore();
  const [copies, setCopies] = useState({});
  const [printing, setPrinting] = useState(false);

  useEffect(() => {
    if (!open) return;
    const init = {};
    (products || []).forEach(p => { init[p.id] = stockOf(p, store) || 1; });
    setCopies(init);
  }, [open, products, store]);

  const setAll = (fn) => {
    const next = {};
    (products || []).forEach(p => { next[p.id] = fn(p); });
    setCopies(next);
  };
  const total = Object.values(copies).reduce((a, b) => a + (Number(b) || 0), 0);
  const storeName = storeNameOf(store);

  const print = async () => {
    const items = (products || [])
      .map(p => ({ name: p.name, barcode: p.barcode || '', sell_price: p.sell_price || 0, copies: Number(copies[p.id]) || 0 }))
      .filter(i => i.copies > 0);
    if (!items.length) { toast.error('Set at least one label to print'); return; }
    setPrinting(true);
    try {
      await printBatchLabels(items);
      onClose();
    } catch (err) {
      toast.error('Print failed: ' + err.message);
    } finally {
      setPrinting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-2xl" data-testid="label-print-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Tag className="w-5 h-5 text-green-600" /> Print Labels</DialogTitle>
          <DialogDescription>
            Copies default to the {storeName} stock count so every unit on the shelf gets a label. Labels print in rows of 3 — leftovers queue until the row fills.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <Button size="sm" variant="outline" onClick={() => setAll(p => stockOf(p, store) || 1)} data-testid="labels-set-stock-btn">
            <Package className="w-3.5 h-3.5 mr-1" /> Match {storeName} stock
          </Button>
          <Button size="sm" variant="outline" onClick={() => setAll(() => 1)} data-testid="labels-set-one-btn">1 each</Button>
          <Button size="sm" variant="outline" onClick={() => setAll(() => 3)} data-testid="labels-set-three-btn">3 each</Button>
          <span className="ml-auto font-semibold text-slate-700" data-testid="labels-total">{total} label{total === 1 ? '' : 's'} · {Math.ceil(total / 3)} row{Math.ceil(total / 3) === 1 ? '' : 's'}</span>
        </div>
        <ScrollArea className="max-h-[50vh] border rounded-md">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500 sticky top-0">
              <tr><th className="text-left px-3 py-2">Product</th><th className="text-right px-3 py-2">{storeName} stock</th><th className="text-right px-3 py-2 w-28">Labels</th></tr>
            </thead>
            <tbody>
              {(products || []).map(p => (
                <tr key={p.id} className="border-t" data-testid={`label-row-${p.id}`}>
                  <td className="px-3 py-1.5">
                    <p className="font-medium truncate max-w-[360px]" title={p.name}>{p.name}</p>
                    <p className="text-xs text-slate-500">{p.barcode || <span className="text-amber-600">no barcode</span>} · ${(p.sell_price || 0).toFixed(2)}</p>
                  </td>
                  <td className="px-3 py-1.5 text-right text-slate-600">{stockOf(p, store)}</td>
                  <td className="px-3 py-1.5 text-right">
                    <Input
                      type="number" min="0" max="999"
                      value={copies[p.id] ?? ''}
                      onChange={(e) => setCopies(c => ({ ...c, [p.id]: Math.max(0, Math.min(999, parseInt(e.target.value || '0', 10))) }))}
                      className="h-8 w-20 text-right ml-auto"
                      data-testid={`label-copies-${p.id}`}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </ScrollArea>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="labels-cancel-btn">Cancel</Button>
          <Button onClick={print} disabled={printing || total === 0} className="bg-green-600 hover:bg-green-700" data-testid="labels-print-btn">
            {printing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Tag className="w-4 h-4 mr-1" />} Print {total} label{total === 1 ? '' : 's'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
